/**
 * 色彩范围选取（选择→色彩范围…，DESIGN.md 对比 Photoshop 补全）。
 *
 * 跟 `selection-grow.ts` 的 growSelection/similarSelection 同款模式：
 * 在文档合成结果上算新 mask、写回 `store.setSelection`——选区是应用层
 * 会话状态，不进 Command Bus、不进历史。
 */
import { colorRangeMask, invert } from '../engine/selection/ops';
import { compositeDocument } from './composite';
import type { SelectionMask } from '../engine/selection/mask';
import type { AppStore } from './store';

/**
 * 应用一次色彩范围选取。`existing` 传打开对话框那一刻的选区，用来限制
 * 色彩范围只在那个范围内生效（真实 Photoshop"色彩范围"尊重当前选区的
 * 规则）；没有选区就传 null，对整图生效。没有文档时什么都不做，返回
 * false。
 *
 * `isStale`：对话框是"边调边看"的（容差滑杆拖动时连续触发重算），每次
 * 输入都会起一次新的异步计算，慢的那次可能在快的那次已经写回选区之后
 * 才算完——不检查的话，会出现"手指已经停在新的容差上，选区却被一次
 * 更早、更慢、已经过期的计算结果盖回去"这种倒退闪烁，取消对话框时
 * 更严重（选区被正确复原后，一个还没算完的旧计算完事了又把它盖掉）。
 * 调用方在结果算出来、真正写回 store 之前用它确认这次调用还作不作数，
 * 过期就直接丢弃，不写。
 */
export async function applyColorRangeSelection(
  store: AppStore,
  refColor: readonly [number, number, number],
  fuzziness: number,
  doInvert: boolean,
  existing: SelectionMask | null,
  isStale?: () => boolean,
): Promise<boolean> {
  const document = store.document;
  if (document === null) return false;
  const composite = compositeDocument(document, store.tiles);
  const mask = await colorRangeMask(composite, refColor, fuzziness, existing);
  if (isStale?.() === true) return false;
  store.setSelection(doInvert ? invert(mask) : mask);
  return true;
}
